import React, { useEffect, useState, useMemo } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import TimePicker from 'react-time-picker';
import 'react-time-picker/dist/TimePicker.css';
import {
  FiArrowLeft,
  FiSave,
  FiLoader,
  FiMapPin,
  FiMap,
  FiPhone,
  FiPlus,
  FiTrash2,
  FiInfo,
  FiLink,
  FiClock,
  FiExternalLink,
} from 'react-icons/fi';
import { TbArrowsMoveVertical } from 'react-icons/tb';
import Layout from '../components/Layout';
import theme from '../styles/theme';
import { clienteSobreAPI, ibgeAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const DIAS_DA_SEMANA = [
  { value: 1, label: 'Domingo' },
  { value: 2, label: 'Segunda-feira' },
  { value: 3, label: 'Terça-feira' },
  { value: 4, label: 'Quarta-feira' },
  { value: 5, label: 'Quinta-feira' },
  { value: 6, label: 'Sexta-feira' },
  { value: 7, label: 'Sábado' },
];

const criarHorariosPadrao = () =>
  DIAS_DA_SEMANA.map(({ value }) => ({
    dia_semana: value,
    aberto: false,
    hora_inicio_1: '',
    hora_fim_1: '',
    hora_inicio_2: '',
    hora_fim_2: '',
  }));

const possuiHorariosPreenchidos = (horario) => {
  if (!horario) return false;

  const inicio1 = horario.hora_inicio_1?.trim();
  const fim1 = horario.hora_fim_1?.trim();
  const inicio2 = horario.hora_inicio_2?.trim();
  const fim2 = horario.hora_fim_2?.trim();

  const possuiPrimeiroPeriodo = inicio1 && fim1;
  const possuiSegundoPeriodo = inicio2 && fim2;

  return Boolean(possuiPrimeiroPeriodo || possuiSegundoPeriodo);
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
  max-width: 960px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${theme.colors.border.primary};
  background: transparent;
  color: ${theme.colors.text.secondary};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: ${theme.colors.primary[500]};
    border-color: ${theme.colors.primary[500]};
  }
`;

const Title = styled.h1`
  margin: 0;
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes['2xl']};
  font-weight: 700;
`;

const Form = styled.form`
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing[8]};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.lg};
  font-weight: 600;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: ${theme.spacing[4]};
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const Label = styled.label`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[1]};
`;

const Required = styled.span`
  color: ${theme.colors.error[500]};
`;

const Select = styled.select`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.sm};
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Input = styled.input`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.sm};
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }
`;

const InputWithIcon = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  width: 100%;

  input {
    flex: 1;
  }
`;

const MapsIconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[3]};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.secondary};
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 44px;
  height: 44px;
  flex-shrink: 0;

  &:hover {
    background: ${theme.colors.background.secondary};
    color: ${theme.colors.primary[500]};
    border-color: ${theme.colors.primary[500]};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const Textarea = styled.textarea`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.sm};
  transition: all 0.2s ease;
  resize: vertical;
  min-height: 140px;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }
`;

const HelperText = styled.span`
  color: ${theme.colors.text.muted};
  font-size: ${theme.fontSizes.xs};
`;

const TelefoneList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
`;

const TelefoneRow = styled.div`
  display: grid;
  grid-template-columns: ${props => props.$hasDragHandle ? '32px 120px 1fr 1fr auto' : '120px 1fr 1fr auto'};
  gap: ${theme.spacing[3]};
  align-items: start;
  padding: ${theme.spacing[2]} 0;
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${props => props.$isDragging ? theme.colors.primary[500] : 'transparent'};
  background: ${props => {
    if (props.$isDragging) return `${theme.colors.primary[500]}20`;
    if (props.$isDragOver) return `${theme.colors.primary[500]}10`;
    return 'transparent';
  }};
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.$hasDragHandle && !props.$isDragging && !props.$isDragOver ? theme.colors.background.secondary : 'inherit'};
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const DragHandle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${props => props.draggable ? 'grab' : 'default'};
  color: ${theme.colors.text.muted};
  padding: 0;
  border-radius: ${theme.borderRadius.md};
  transition: all 0.2s ease;
  user-select: none;
  width: 32px;
  height: 48px;
  margin-top: 24px;
  flex-shrink: 0;

  &:hover {
    color: ${theme.colors.primary[500]};
    background: ${theme.colors.background.secondary};
  }

  &:active {
    cursor: ${props => props.draggable ? 'grabbing' : 'default'};
  }

  &[draggable="true"] {
    cursor: grab;
  }

  &[draggable="true"]:active {
    cursor: grabbing;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;


const TelefoneActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[2]};
  margin-top: 28px;
  height: 48px;
`;

const SmallButton = styled.button`
  padding: ${props => props.$iconOnly ? theme.spacing[2] : `${theme.spacing[2]} ${theme.spacing[3]}`};
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${props => (props.$danger ? theme.colors.error[500] : theme.colors.primary[500])};
  background: ${props => (props.$danger ? 'transparent' : theme.colors.primary[500])};
  color: ${props => (props.$danger ? theme.colors.error[500] : theme.colors.text.primary)};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${props => props.$iconOnly ? '0' : theme.spacing[1]};
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: ${props => props.$iconOnly ? '32px' : 'auto'};
  height: ${props => props.$iconOnly ? '32px' : 'auto'};

  svg {
    width: 18px;
    height: 18px;
  }

  &:hover {
    background: ${props => (props.$danger ? theme.colors.error[500] : theme.colors.primary[600])};
    color: ${theme.colors.text.primary};
  }
`;

const HorariosContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
  position: relative;
  overflow: visible;
`;

const HorariosTitle = styled.h3`
  margin: 0;
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.lg};
  font-weight: 600;
`;

const DaysSelector = styled.div`
  display: flex;
  gap: ${theme.spacing[2]};
  flex-wrap: wrap;
`;

const DayButton = styled.button`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 2px solid ${props => {
    if (props.$selected) return theme.colors.primary[500];
    if (props.$aberto) return theme.colors.primary[400];
    return theme.colors.border.primary;
  }};
  background: ${props => {
    if (props.$selected) return theme.colors.primary[500];
    if (props.$aberto) return theme.colors.primary[500];
    return theme.colors.background.primary;
  }};
  color: ${props => {
    if (props.$selected || props.$aberto) return theme.colors.text.primary;
    return theme.colors.text.secondary;
  }};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:enabled {
    border-color: ${theme.colors.primary[500]};
    background: ${props => {
      if (props.$selected) return theme.colors.primary[600];
      if (props.$aberto) return theme.colors.primary[600];
      return theme.colors.primary[50];
    }};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const HorariosOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
`;

const CheckboxOption = styled.label`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
  cursor: pointer;

  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    accent-color: ${theme.colors.primary[500]};
    cursor: pointer;
  }
`;

const HorariosList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
`;

const HorarioItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[4]};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.background.primary};
  position: relative;
  overflow: visible;
`;

const HorarioItemHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const HorarioItemTitle = styled.span`
  font-weight: 600;
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};
`;

const RemoveHorarioButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${theme.colors.error[500]};
  background: transparent;
  color: ${theme.colors.error[500]};
  font-size: ${theme.fontSizes.base};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  align-self: center;
  margin-top: ${theme.spacing[5]};

  svg {
    width: 18px;
    height: 18px;
  }

  &:hover:enabled {
    background: ${theme.colors.error[500]};
    color: ${theme.colors.text.primary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const HorarioFields = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  gap: ${theme.spacing[3]};
  align-items: end;
`;

const HorarioField = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[1]};
`;


const HorarioFieldLabel = styled.label`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.xs};
  font-weight: 600;
`;

const TimePickerWrapper = styled.div`
  position: relative;
  width: 100%;
  z-index: 1;

  .react-time-picker {
    width: 100%;
    position: relative;
  }

  .react-time-picker__wrapper {
    padding: ${theme.spacing[3]} ${theme.spacing[4]};
    border: 1px solid ${theme.colors.border.primary};
    border-radius: ${theme.borderRadius.lg};
    background: ${theme.colors.background.primary};
    color: ${theme.colors.text.primary};
    font-size: ${theme.fontSizes.sm};
    transition: all 0.2s ease;
  }

  .react-time-picker__wrapper:hover {
    border-color: ${theme.colors.primary[500]};
  }

  .react-time-picker__wrapper--enabled:focus-within {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }

  .react-time-picker__inputGroup {
    color: ${theme.colors.text.primary};
  }

  .react-time-picker__inputGroup__input {
    color: ${theme.colors.text.primary};
    background: transparent;
    cursor: text !important;
  }

  .react-time-picker__inputGroup__input:invalid {
    background: transparent;
  }

  .react-time-picker__inputGroup__input:focus {
    outline: none;
  }

  .react-time-picker__button {
    color: ${theme.colors.text.secondary};
  }

  .react-time-picker__button:hover {
    color: ${theme.colors.primary[500]};
  }

  .react-time-picker__button:enabled:active {
    color: ${theme.colors.primary[500]};
  }


  .react-time-picker--disabled .react-time-picker__wrapper {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const AddHorarioButton = styled.button`
  align-self: flex-start;
  padding: ${theme.spacing[2]} ${theme.spacing[4]};
  border: none;
  background: transparent;
  color: ${theme.colors.primary[500]};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[1]};

  &:hover:enabled {
    color: ${theme.colors.primary[600]};
    text-decoration: underline;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: ${theme.spacing[4]};
  justify-content: flex-end;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: ${theme.spacing[2]};
  }
`;

const Button = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  border: 1px solid ${props => {
    if (props.$danger) return theme.colors.error[500];
    if (props.$success) return theme.colors.success[500];
    if (props.$primary) return theme.colors.primary[500];
    return theme.colors.border.primary;
  }};
  background: ${props => {
    if (props.$danger) return theme.colors.error[500];
    if (props.$success) return theme.colors.success[500];
    if (props.$primary) return theme.colors.primary[500];
    return 'transparent';
  }};
  color: ${props => {
    if (props.$danger || props.$success) return theme.colors.text.primary;
    if (props.$primary) return theme.colors.text.primary;
    return theme.colors.text.secondary;
  }};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[2]};

  &:hover:not(:disabled) {
    background: ${props => {
      if (props.$danger) return theme.colors.error[600];
      if (props.$success) return theme.colors.success[600];
      if (props.$primary) return theme.colors.primary[600];
      return theme.colors.background.primary;
    }};
    color: ${props => {
      if (props.$danger || props.$success) return theme.colors.text.primary;
      if (props.$primary) return theme.colors.text.primary;
      return theme.colors.error[500];
    }};
    border-color: ${props => {
      if (props.$danger) return theme.colors.error[600];
      if (props.$success) return theme.colors.success[600];
      if (props.$primary) return theme.colors.primary[600];
      return theme.colors.error[500];
    }};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    width: 100%;
    padding: ${theme.spacing[3]} ${theme.spacing[4]};
    font-size: ${theme.fontSizes.sm};
  }
`;

// Modal de confirmação
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: ${theme.colors.background.secondary};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing[8]};
  max-width: 500px;
  width: 90%;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);

  @media (max-width: 768px) {
    padding: ${theme.spacing[4]};
    width: 95%;
    max-width: none;
  }
`;

const ModalTitle = styled.h3`
  color: ${theme.colors.error[500]};
  font-size: ${theme.fontSizes.xl};
  font-weight: 700;
  margin: 0 0 ${theme.spacing[4]} 0;
`;

const ModalMessage = styled.p`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.base};
  line-height: 1.5;
  margin: 0 0 ${theme.spacing[6]} 0;
`;

const ModalActions = styled.div`
  display: flex;
  gap: ${theme.spacing[3]};
  justify-content: flex-end;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: ${theme.spacing[2]};
  }
`;

const ModalButton = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  border: 1px solid ${props => props.$danger ? theme.colors.error[500] : theme.colors.border.primary};
  background: ${props => props.$danger ? theme.colors.error[500] : 'transparent'};
  color: ${props => props.$danger ? theme.colors.text.primary : theme.colors.text.secondary};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[2]};

  &:hover:not(:disabled) {
    background: ${props => props.$danger ? theme.colors.error[600] : theme.colors.background.primary};
    border-color: ${props => props.$danger ? theme.colors.error[600] : theme.colors.primary[500]};
    color: ${props => props.$danger ? theme.colors.text.primary : theme.colors.primary[500]};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const ErrorMessage = styled.div`
  padding: ${theme.spacing[4]};
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${theme.colors.error[400]};
  background: ${theme.colors.error[500]}20;
  color: ${theme.colors.error[500]};
  font-size: ${theme.fontSizes.sm};
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[12]} ${theme.spacing[6]};
  color: ${theme.colors.text.muted};
`;

const LoadingSpinner = styled.div`
  font-size: ${theme.fontSizes['2xl']};
  animation: spin 1.2s linear infinite;
  margin-bottom: ${theme.spacing[3]};

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const ClienteSobreForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { isAdmin } = useAuth();
  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState({
    cliente_id: '',
    descricao: '',
    logradouro: '',
    numero: '',
    bairro: '',
    cidade: '',
    estado: '',
    complemento: '',
    google_maps_url: '',
    waze_url: '',
  });
  const [telefones, setTelefones] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loadingClientes, setLoadingClientes] = useState(false);
  const [loadingRegistro, setLoadingRegistro] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [estados, setEstados] = useState([]);
  const [cidades, setCidades] = useState([]);
  const [registroCarregado, setRegistroCarregado] = useState(null);
  const [horarios, setHorarios] = useState(() => criarHorariosPadrao());
  const [diaSelecionado, setDiaSelecionado] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [mapsWindow, setMapsWindow] = useState(null);

  const descricaoLength = useMemo(() => formData.descricao?.length || 0, [formData.descricao]);

  const formatarNumeroTelefone = (value) => {
    const apenasNumeros = String(value || '').replace(/\D/g, '').slice(0, 9);
    if (!apenasNumeros) return '';
    // Formato: 8 dígitos -> 9999-9999, 9 dígitos -> 99999-9999
    if (apenasNumeros.length <= 4) return apenasNumeros;
    if (apenasNumeros.length <= 8) {
      return `${apenasNumeros.slice(0, 4)}-${apenasNumeros.slice(4)}`;
    }
    return `${apenasNumeros.slice(0, 5)}-${apenasNumeros.slice(5)}`;
  };

  const carregarClientes = async () => {
    try {
      setLoadingClientes(true);
      const response = await clienteSobreAPI.listarClientes();
      if (response.data.success) {
        const clientesList = response.data.data || [];
        setClientes(clientesList);
        
        // Se não for admin, não estiver editando e tiver apenas 1 cliente, pré-selecionar automaticamente
        if (!isEditMode && !isAdmin() && clientesList.length === 1) {
          setFormData((prev) => ({
            ...prev,
            cliente_id: clientesList[0].id,
          }));
        }
      } else {
        setClientes([]);
      }
    } catch (err) {
      console.error('Erro ao carregar clientes para seleção:', err);
      setClientes([]);
    } finally {
      setLoadingClientes(false);
    }
  };

  const carregarRegistro = async () => {
    if (!isEditMode) return;
    try {
      setLoadingRegistro(true);
      const response = await clienteSobreAPI.buscarPorId(id);
      if (response.data.success && response.data.data) {
        const registro = response.data.data;
        setRegistroCarregado(registro);
        
        setFormData({
          cliente_id: registro.cliente_id ?? '',
          descricao: registro.descricao ?? '',
          logradouro: registro.logradouro ?? '',
          numero: registro.numero ?? '',
          bairro: registro.bairro ?? '',
          cidade: registro.cidade ?? '',
          estado: registro.estado ?? '',
          complemento: registro.complemento ?? '',
          google_maps_url: registro.google_maps_url ?? '',
          waze_url: registro.waze_url ?? '',
        });
        setTelefones(
          Array.isArray(registro.telefones) && registro.telefones.length > 0
            ? registro.telefones
                .sort((a, b) => {
                  const prioridadeA = a.prioridade !== undefined ? a.prioridade : 999;
                  const prioridadeB = b.prioridade !== undefined ? b.prioridade : 999;
                  if (prioridadeA !== prioridadeB) {
                    return prioridadeA - prioridadeB;
                  }
                  return (a.id || 0) - (b.id || 0);
                })
                .map((telefone, index) => ({
                  ddd: telefone.ddd || '',
                  numero: telefone.numero ? formatarNumeroTelefone(telefone.numero) : '',
                  contato: telefone.contato || '',
                  prioridade: telefone.prioridade !== undefined ? telefone.prioridade : index,
                }))
            : []
        );
        const horariosMapeados = mapHorariosFromApi(registro.horarios);
        setHorarios(horariosMapeados);
        
        // Selecionar automaticamente o primeiro dia com horário aberto
        const primeiroDiaAberto = horariosMapeados.find((h) => h.aberto);
        if (primeiroDiaAberto) {
          setDiaSelecionado(primeiroDiaAberto.dia_semana);
        }
      } else {
        setError('Informação da revenda não encontrada');
      }
    } catch (err) {
      console.error('Erro ao carregar registro de cliente_sobre:', err);
      setError(err.response?.data?.message || 'Erro ao carregar dados');
    } finally {
      setLoadingRegistro(false);
    }
  };

  // Normalizar estado quando os estados forem carregados e houver um registro carregado
  useEffect(() => {
    if (isEditMode && registroCarregado && estados.length > 0 && formData.estado) {
      const estadoAtual = formData.estado;
      
      // Verificar se já está normalizado (é uma sigla válida)
      const estadoEncontrado = estados.find(
        (e) => e.sigla.toUpperCase() === estadoAtual.toUpperCase()
      );
      
      // Se não encontrou por sigla, tentar encontrar por nome e normalizar
      if (!estadoEncontrado) {
        const estadoPorNome = estados.find(
          (e) => e.nome.toUpperCase() === estadoAtual.toUpperCase()
        );
        if (estadoPorNome) {
          setFormData((prev) => ({
            ...prev,
            estado: estadoPorNome.sigla,
          }));
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estados, registroCarregado, isEditMode, formData.estado]);

  useEffect(() => {
    carregarClientes();
    carregarRegistro();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);


  useEffect(() => {
    if (!isEditMode) {
      setHorarios(criarHorariosPadrao());
    }
  }, [isEditMode]);

  // Carregar lista de estados (IBGE)
  useEffect(() => {
    const carregarEstados = async () => {
      try {
        const resp = await ibgeAPI.listarEstados();
        const ordenados = (resp.data || []).sort((a, b) => a.nome.localeCompare(b.nome));
        setEstados(ordenados);
      } catch (e) {
        console.error('Erro ao carregar estados IBGE:', e);
      }
    };
    carregarEstados();
  }, []);

  // Carregar cidades ao alterar o estado (IBGE)
  useEffect(() => {
    const carregarCidades = async (uf) => {
      try {
        const resp = await ibgeAPI.listarMunicipios(uf);
        const ordenadas = (resp.data || []).sort((a, b) => a.nome.localeCompare(b.nome));
        setCidades(ordenadas);
      } catch (e) {
        console.error('Erro ao carregar municípios IBGE:', e);
      }
    };

    if (formData.estado) {
      carregarCidades(formData.estado);
    } else {
      setCidades([]);
    }
  }, [formData.estado]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleOpenGoogleMaps = () => {
    // Construir URL do Google Maps com base no endereço preenchido
    const endereco = [
      formData.logradouro,
      formData.numero,
      formData.bairro,
      formData.cidade,
      formData.estado,
    ]
      .filter(Boolean)
      .join(', ');

    // Se houver endereço, usar para buscar o local, senão abrir o mapa vazio
    const mapsUrl = endereco
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(endereco)}`
      : 'https://www.google.com/maps';

    // Abrir Google Maps em nova janela
    const newMapsWindow = window.open(mapsUrl, '_blank', 'width=1200,height=800');
    setMapsWindow(newMapsWindow);

    // Monitorar quando o usuário compartilha o link
    // Nota: Devido a políticas de segurança do navegador, não é possível acessar
    // diretamente a URL do Google Maps de uma janela externa. A solução é:
    // 1. O usuário seleciona o local no Google Maps
    // 2. Clica com o botão direito e seleciona "Compartilhar" ou "O que há aqui?"
    // 3. Copia o link compartilhado
    // 4. Cola o link no campo ao lado

    // Alternativa: Usar a API do Google Maps Places (requer API key)
    // Para uma solução mais automatizada, seria necessário integrar com a API do Google Maps

    if (newMapsWindow) {
      // Verificar periodicamente se a janela foi fechada
      const checkInterval = setInterval(() => {
        if (newMapsWindow.closed) {
          clearInterval(checkInterval);
          setMapsWindow(null);
        }
      }, 1000);

      // Limpar intervalo após 5 minutos
      setTimeout(() => {
        clearInterval(checkInterval);
      }, 300000);
    }
  };

  const handleOpenWaze = () => {
    // Abrir o site web do Waze (mapa ao vivo)
    // O Waze web não tem uma URL de busca direta como o Google Maps,
    // então abrimos o mapa e o usuário pode buscar manualmente
    const wazeUrl = 'https://www.waze.com/live-map';

    // Abrir Waze em nova janela
    const newWazeWindow = window.open(wazeUrl, '_blank', 'width=1200,height=800');
    setMapsWindow(newWazeWindow);

    // Monitorar quando o usuário compartilha o link
    // Nota: Devido a políticas de segurança do navegador, não é possível acessar
    // diretamente a URL do Waze de uma janela externa. A solução é:
    // 1. O usuário seleciona o local no Waze
    // 2. Clica com o botão direito e seleciona "Compartilhar" ou "Copiar link"
    // 3. Copia o link compartilhado
    // 4. Cola o link no campo ao lado

    if (newWazeWindow) {
      // Verificar periodicamente se a janela foi fechada
      const checkInterval = setInterval(() => {
        if (newWazeWindow.closed) {
          clearInterval(checkInterval);
          setMapsWindow(null);
        }
      }, 1000);

      // Limpar intervalo após 5 minutos
      setTimeout(() => {
        clearInterval(checkInterval);
      }, 300000);
    }
  };

  const handleEstadoChange = (uf) => {
    setFormData((prev) => ({
      ...prev,
      estado: uf,
      cidade: '',
    }));
  };

  const handleDiaToggle = (diaSemana) => {
    setHorarios((prev) =>
      prev.map((horario) => {
        // Ao sair do dia atual, se não houver horários preenchidos, marcar como fechado
        if (diaSelecionado && horario.dia_semana === diaSelecionado) {
          if (!possuiHorariosPreenchidos(horario)) {
            return { ...horario, aberto: false };
          }
        }

        if (horario.dia_semana === diaSemana) {
          const possuiHorarios = possuiHorariosPreenchidos(horario);
          return {
            ...horario,
            aberto: possuiHorarios ? horario.aberto : true,
          };
        }

        return horario;
      })
    );

    setDiaSelecionado(diaSemana);
  };

  const handleHorarioToggle = (diaSemana, aberto) => {
    setHorarios((prev) => {
      const updated = prev.map((horario) =>
        horario.dia_semana === diaSemana
          ? {
              ...horario,
              aberto,
              hora_inicio_1: aberto ? horario.hora_inicio_1 : '',
              hora_fim_1: aberto ? horario.hora_fim_1 : '',
              hora_inicio_2: aberto ? horario.hora_inicio_2 : '',
              hora_fim_2: aberto ? horario.hora_fim_2 : '',
            }
          : horario
      );
      return updated;
    });
  };

  const handleHorarioTimeChange = (diaSemana, campo, valor) => {
    // Converter o valor para string no formato HH:mm se for um objeto Date ou string
    let valorFormatado = '';
    if (valor) {
      if (typeof valor === 'string') {
        valorFormatado = valor;
      } else if (valor instanceof Date) {
        const horas = String(valor.getHours()).padStart(2, '0');
        const minutos = String(valor.getMinutes()).padStart(2, '0');
        valorFormatado = `${horas}:${minutos}`;
      } else {
        valorFormatado = String(valor);
      }
    }
    
    setHorarios((prev) =>
      prev.map((horario) =>
        horario.dia_semana === diaSemana
          ? {
              ...horario,
              [campo]: valorFormatado,
            }
          : horario
      )
    );
  };

  const adicionarHorario = (diaSemana) => {
    setHorarios((prev) =>
      prev.map((horario) => {
        if (horario.dia_semana === diaSemana) {
          // Se não tem primeiro horário, adiciona
          if (!horario.hora_inicio_1 || !horario.hora_fim_1) {
            return {
              ...horario,
              hora_inicio_1: horario.hora_inicio_1 || '09:00',
              hora_fim_1: horario.hora_fim_1 || '12:00',
            };
          }
          // Se tem primeiro mas não tem segundo, adiciona segundo
          if (!horario.hora_inicio_2 || !horario.hora_fim_2) {
            return {
              ...horario,
              hora_inicio_2: horario.hora_inicio_2 || '14:00',
              hora_fim_2: horario.hora_fim_2 || '18:00',
            };
          }
        }
        return horario;
      })
    );
  };

  const removerHorario = (diaSemana, index) => {
    setHorarios((prev) =>
      prev.map((horario) => {
        if (horario.dia_semana === diaSemana) {
          if (index === 1) {
            // Remove primeiro horário, move segundo para primeiro
            return {
              ...horario,
              hora_inicio_1: horario.hora_inicio_2 || '',
              hora_fim_1: horario.hora_fim_2 || '',
              hora_inicio_2: '',
              hora_fim_2: '',
            };
          } else if (index === 2) {
            // Remove segundo horário
            return {
              ...horario,
              hora_inicio_2: '',
              hora_fim_2: '',
            };
          }
        }
        return horario;
      })
    );
  };

  const getHorariosDoDia = (diaSemana) => {
    const horario = horarios.find((h) => h.dia_semana === diaSemana);
    if (!horario || !horario.aberto) return [];
    
    const lista = [];
    if (horario.hora_inicio_1 && horario.hora_fim_1) {
      lista.push({
        index: 1,
        inicio: horario.hora_inicio_1,
        fim: horario.hora_fim_1,
      });
    }
    if (horario.hora_inicio_2 && horario.hora_fim_2) {
      lista.push({
        index: 2,
        inicio: horario.hora_inicio_2,
        fim: horario.hora_fim_2,
      });
    }
    return lista;
  };

  const podeAdicionarHorario = (diaSemana) => {
    const horariosDoDia = getHorariosDoDia(diaSemana);
    return horariosDoDia.length < 2;
  };

  const handleTelefoneChange = (index, field, value) => {
    setTelefones((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        [field]: value,
      };
      return updated;
    });
  };

  const handleTelefoneDDDChange = (index, value) => {
    // Permitir apenas números e limitar estritamente a 2 dígitos
    const apenasNumeros = value.replace(/\D/g, '').slice(0, 2);
    // Sempre limitar a exatamente 2 dígitos, mesmo se tentar digitar mais
    handleTelefoneChange(index, 'ddd', apenasNumeros);
  };

  const handleTelefoneNumeroChange = (index, value) => {
    // Aplicar formatação de telefone
    const valorFormatado = formatarNumeroTelefone(value);
    handleTelefoneChange(index, 'numero', valorFormatado);
  };

  const adicionarTelefone = () => {
    setTelefones((prev) => {
      const maxPrioridade = prev.length > 0 
        ? Math.max(...prev.map(t => t.prioridade !== undefined ? t.prioridade : 0))
        : -1;
      return [...prev, { ddd: '', numero: '', contato: '', prioridade: maxPrioridade + 1 }];
    });
  };

  const removerTelefone = (index) => {
    setTelefones((prev) => {
      const updated = prev.filter((_, idx) => idx !== index);
      // Reordenar prioridades após remoção
      return updated.map((tel, idx) => ({
        ...tel,
        prioridade: idx,
      }));
    });
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());
    // Aplicar estilo à linha inteira
    const row = e.target.closest('[data-telefone-row]');
    if (row) {
      row.style.opacity = '0.5';
    }
  };

  const handleDragEnd = (e) => {
    // Restaurar opacidade
    const row = e.target.closest('[data-telefone-row]');
    if (row) {
      row.style.opacity = '1';
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    setTelefones((prev) => {
      // Ordenar por prioridade para trabalhar com a ordem visual
      const sorted = [...prev].sort((a, b) => {
        const prioridadeA = a.prioridade !== undefined ? a.prioridade : 999;
        const prioridadeB = b.prioridade !== undefined ? b.prioridade : 999;
        return prioridadeA - prioridadeB;
      });

      const draggedItem = sorted[draggedIndex];
      
      // Remover o item arrastado
      sorted.splice(draggedIndex, 1);
      
      // Inserir na nova posição
      sorted.splice(dropIndex, 0, draggedItem);
      
      // Atualizar prioridades baseado na nova ordem
      return sorted.map((tel, idx) => ({
        ...tel,
        prioridade: idx,
      }));
    });

    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const ajustarTelefones = (list) =>
    list
      .map((telefone, index) => ({
        ddd: telefone.ddd?.trim() || '',
        numero: telefone.numero?.replace(/\D/g, '') || '', // Remover formatação, manter apenas números
        contato: telefone.contato?.trim() || '',
        prioridade: telefone.prioridade !== undefined ? telefone.prioridade : index,
      }))
      .filter((telefone) => telefone.ddd || telefone.numero || telefone.contato);

const mapHorariosFromApi = (apiHorarios = []) => {
  const base = criarHorariosPadrao();
  const mapa = new Map(
    apiHorarios.map((horario) => [
      Number.parseInt(horario.dia_semana ?? horario.diaSemana ?? horario.dia ?? '', 10),
      horario,
    ])
  );

  return base.map((padrao) => {
    const existente = mapa.get(padrao.dia_semana);
    if (!existente) {
      return padrao;
    }
    return {
      dia_semana: padrao.dia_semana,
      aberto: existente.aberto ?? true,
      hora_inicio_1: existente.hora_inicio_1 ? existente.hora_inicio_1.slice(0, 5) : '',
      hora_fim_1: existente.hora_fim_1 ? existente.hora_fim_1.slice(0, 5) : '',
      hora_inicio_2: existente.hora_inicio_2 ? existente.hora_inicio_2.slice(0, 5) : '',
      hora_fim_2: existente.hora_fim_2 ? existente.hora_fim_2.slice(0, 5) : '',
    };
  });
};

const ajustarHorarios = (list) =>
  list.map((horario) => ({
    dia_semana: Number.parseInt(horario.dia_semana, 10),
    aberto: Boolean(horario.aberto),
    hora_inicio_1:
      horario.aberto && horario.hora_inicio_1 ? horario.hora_inicio_1 : null,
    hora_fim_1:
      horario.aberto && horario.hora_fim_1 ? horario.hora_fim_1 : null,
    hora_inicio_2:
      horario.aberto && horario.hora_inicio_2 ? horario.hora_inicio_2 : null,
    hora_fim_2:
      horario.aberto && horario.hora_fim_2 ? horario.hora_fim_2 : null,
  }));

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);

    if (!formData.cliente_id) {
      setError('É necessário selecionar um cliente.');
      return;
    }

    if (formData.descricao && formData.descricao.length > 5000) {
      setError('A descrição deve conter no máximo 5000 caracteres.');
      return;
    }

    const horariosNormalizados = horarios.map((horario) => {
      const possuiHorarios = possuiHorariosPreenchidos(horario);
      return {
        ...horario,
        aberto: possuiHorarios ? horario.aberto : false,
      };
    });

    // Atualiza estado para refletir dias sem horários como fechados
    setHorarios(horariosNormalizados);

    const payload = {
      ...formData,
      cliente_id: formData.cliente_id,
      telefones: ajustarTelefones(telefones),
      horarios: ajustarHorarios(horariosNormalizados),
    };

    try {
      setSaving(true);

      if (isEditMode) {
        await clienteSobreAPI.atualizar(id, payload);
      } else {
        await clienteSobreAPI.criar(payload);
      }

      navigate('/cliente-sobre');
    } catch (err) {
      console.error('Erro ao salvar cliente_sobre:', err);
      setError(err.response?.data?.message || 'Erro ao salvar informações');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/cliente-sobre');
  };

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
  };

  const handleDelete = async () => {
    if (!isEditMode || !id) return;

    try {
      setDeleting(true);
      setError(null);
      await clienteSobreAPI.deletar(id);
      navigate('/cliente-sobre');
    } catch (err) {
      console.error('Erro ao excluir informação da revenda:', err);
      setError(err.response?.data?.message || 'Erro ao excluir informação');
      setShowDeleteModal(false);
    } finally {
      setDeleting(false);
    }
  };

  if (loadingRegistro) {
    return (
      <Layout title={isEditMode ? 'Editar Informação da Revenda' : 'Nova Informação da Revenda'}>
        <Container>
          <LoadingContainer>
            <LoadingSpinner>
              <FiLoader />
            </LoadingSpinner>
            Carregando dados...
          </LoadingContainer>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout title={isEditMode ? 'Editar Informação da Revenda' : 'Nova Informação da Revenda'}>
      <Container>
        <Header>
          <BackButton type="button" onClick={handleCancel}>
            <FiArrowLeft />
            Voltar
          </BackButton>
          <Title>{isEditMode ? 'Editar Informação da Revenda' : 'Nova Informação da Revenda'}</Title>
        </Header>

        <Form onSubmit={handleSubmit}>
          <Section>
            <SectionHeader>
              <FiInfo />
              Informações Gerais
            </SectionHeader>
            <Grid>
              <Field>
                <Label>
                  Cliente
                  <Required>*</Required>
                </Label>
                <Select
                  value={formData.cliente_id}
                  onChange={(e) => {
                    const { value } = e.target;
                    handleInputChange(
                      'cliente_id',
                      value ? Number.parseInt(value, 10) : ''
                    );
                  }}
                  disabled={
                    loadingClientes ||
                    saving ||
                    isEditMode ||
                    (!isAdmin() && clientes.length === 1)
                  }
                  required
                >
                  <option value="">Selecione um cliente...</option>
                  {clientes.map((cliente) => (
                    <option key={cliente.id} value={cliente.id}>
                      {cliente.nome_fantasia || cliente.nome_social || `Cliente #${cliente.id}`}
                    </option>
                  ))}
                </Select>
                {isEditMode && (
                  <HelperText>
                    Para alterar o cliente, exclua e recrie a informação da revenda.
                  </HelperText>
                )}
                {!isEditMode && !isAdmin() && clientes.length === 1 && (
                  <HelperText>
                    Você tem acesso apenas a este cliente.
                  </HelperText>
                )}
              </Field>
            </Grid>
            <Field>
              <Label>
                Descrição da Revenda
                <Required>*</Required>
              </Label>
              <Textarea
                maxLength={5000}
                value={formData.descricao}
                onChange={(e) => handleInputChange('descricao', e.target.value)}
                required
              />
              <HelperText>{descricaoLength}/5000 caracteres</HelperText>
            </Field>
          </Section>

          <Section>
            <SectionHeader>
              <FiMapPin />
              Localização
            </SectionHeader>
            <Grid>
              <Field>
                <Label>Estado</Label>
                <Select
                  value={formData.estado}
                  onChange={(e) => handleEstadoChange(e.target.value)}
                  disabled={saving}
                >
                  <option value="">Selecione...</option>
                  {estados.map((uf) => (
                    <option key={uf.id} value={uf.sigla}>
                      {`${uf.nome} (${uf.sigla})`}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field>
                <Label>Cidade</Label>
                <Select
                  value={formData.cidade}
                  onChange={(e) => handleInputChange('cidade', e.target.value)}
                  disabled={saving || !formData.estado}
                >
                  <option value="">Selecione...</option>
                  {cidades.map((c) => (
                    <option key={c.id} value={c.nome}>
                      {c.nome}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field>
                <Label>Logradouro</Label>
                <Input
                  type="text"
                  value={formData.logradouro}
                  onChange={(e) => handleInputChange('logradouro', e.target.value)}
                />
              </Field>
              <Field>
                <Label>Número</Label>
                <Input
                  type="text"
                  value={formData.numero}
                  onChange={(e) => {
                    // Permitir apenas números
                    const apenasNumeros = e.target.value.replace(/\D/g, '');
                    handleInputChange('numero', apenasNumeros);
                  }}
                  inputMode="numeric"
                />
              </Field>
              <Field>
                <Label>Bairro</Label>
                <Input
                  type="text"
                  value={formData.bairro}
                  onChange={(e) => handleInputChange('bairro', e.target.value)}
                />
              </Field>
              <Field>
                <Label>Complemento</Label>
                <Input
                  type="text"
                  value={formData.complemento}
                  onChange={(e) => handleInputChange('complemento', e.target.value)}
                />
              </Field>
            </Grid>
          </Section>

          <Section>
            <SectionHeader>
              <FiLink />
              Localização por Aplicativos
            </SectionHeader>
            <Grid>
              <Field>
                <Label>Link do Google Maps</Label>
                <InputWithIcon>
                  <Input
                    type="url"
                    value={formData.google_maps_url}
                    onChange={(e) => handleInputChange('google_maps_url', e.target.value)}
                    placeholder="https://maps.google.com/..."
                  />
                  <MapsIconButton
                    type="button"
                    onClick={handleOpenGoogleMaps}
                    title="Abrir Google Maps para selecionar local"
                  >
                    <FiMap />
                  </MapsIconButton>
                </InputWithIcon>
              </Field>
              <Field>
                <Label>Link do Waze</Label>
                <InputWithIcon>
                  <Input
                    type="url"
                    value={formData.waze_url}
                    onChange={(e) => handleInputChange('waze_url', e.target.value)}
                    placeholder="https://waze.com/..."
                  />
                  <MapsIconButton
                    type="button"
                    onClick={handleOpenWaze}
                    title="Abrir Waze para selecionar local"
                  >
                    <FiMap />
                  </MapsIconButton>
                </InputWithIcon>
              </Field>
            </Grid>
          </Section>

          <Section>
            <SectionHeader>
              <FiClock />
              Horários de Funcionamento
            </SectionHeader>
            <HorariosContainer>
              <HorariosTitle>Selecionar dias e horários</HorariosTitle>
              
              <DaysSelector>
                {DIAS_DA_SEMANA.map((dia) => {
                  const horario = horarios.find((h) => h.dia_semana === dia.value);
                  const isSelected = diaSelecionado === dia.value;
                  const isAberto = horario?.aberto || false;
                  const primeiraLetra = dia.label.charAt(0);
                  
                  return (
                    <DayButton
                      key={dia.value}
                      type="button"
                      $selected={isSelected}
                      $aberto={isAberto}
                      onClick={() => handleDiaToggle(dia.value)}
                      disabled={saving}
                      title={dia.label}
                    >
                      {primeiraLetra}
                    </DayButton>
                  );
                })}
              </DaysSelector>

              {diaSelecionado && (() => {
                const horario = horarios.find((h) => h.dia_semana === diaSelecionado);
                if (!horario) return null;
                
                const infoDia = DIAS_DA_SEMANA.find((d) => d.value === diaSelecionado);
                const horariosDoDia = getHorariosDoDia(diaSelecionado);
                
                return (
                  <HorarioItem>
                    <HorarioItemHeader>
                      <HorarioItemTitle>{infoDia?.label || `Dia ${diaSelecionado}`}</HorarioItemTitle>
                      <CheckboxOption>
                        <input
                          type="checkbox"
                          checked={!horario.aberto}
                          onChange={(e) => handleHorarioToggle(diaSelecionado, !e.target.checked)}
                          disabled={saving}
                        />
                        <span>Fechado</span>
                      </CheckboxOption>
                    </HorarioItemHeader>

                    <HorariosList>
                      {horariosDoDia.map((horarioItem) => {
                        return (
                          <HorarioFields key={horarioItem.index}>
                            <HorarioField>
                              <HorarioFieldLabel>Horário de abertura</HorarioFieldLabel>
                              <TimePickerWrapper>
                                <TimePicker
                                  value={horarioItem.inicio || null}
                                  onChange={(value) => {
                                    handleHorarioTimeChange(
                                      diaSelecionado,
                                      `hora_inicio_${horarioItem.index}`,
                                      value || ''
                                    );
                                  }}
                                  disabled={saving || !horario.aberto}
                                  disableClock={true}
                                  format="HH:mm"
                                  clearIcon={null}
                                  clockIcon={null}
                                />
                              </TimePickerWrapper>
                            </HorarioField>
                            <HorarioField>
                              <HorarioFieldLabel>Horário de fechamento</HorarioFieldLabel>
                              <TimePickerWrapper>
                                <TimePicker
                                  value={horarioItem.fim || null}
                                  onChange={(value) => {
                                    handleHorarioTimeChange(
                                      diaSelecionado,
                                      `hora_fim_${horarioItem.index}`,
                                      value || ''
                                    );
                                  }}
                                  disabled={saving || !horario.aberto}
                                  disableClock={true}
                                  format="HH:mm"
                                  clearIcon={null}
                                  clockIcon={null}
                                />
                              </TimePickerWrapper>
                            </HorarioField>
                          <RemoveHorarioButton
                            type="button"
                            onClick={() => removerHorario(diaSelecionado, horarioItem.index)}
                            disabled={saving}
                            title="Remover horário"
                          >
                            <FiTrash2 />
                          </RemoveHorarioButton>
                        </HorarioFields>
                      );
                      })}
                    </HorariosList>

                    {horario.aberto && podeAdicionarHorario(diaSelecionado) && (
                      <AddHorarioButton
                        type="button"
                        onClick={() => adicionarHorario(diaSelecionado)}
                        disabled={saving}
                      >
                        <FiPlus />
                        Adicionar horário
                      </AddHorarioButton>
                    )}
                  </HorarioItem>
                );
              })()}
            </HorariosContainer>
          </Section>

          <Section>
            <SectionHeader>
              <FiPhone />
              Telefones de Contato
            </SectionHeader>
            <TelefoneList>
              {telefones.length === 0 && (
                <HelperText>Nenhum telefone cadastrado. Adicione um telefone abaixo.</HelperText>
              )}

              {telefones
                .slice()
                .sort((a, b) => {
                  const prioridadeA = a.prioridade !== undefined ? a.prioridade : 999;
                  const prioridadeB = b.prioridade !== undefined ? b.prioridade : 999;
                  return prioridadeA - prioridadeB;
                })
                .map((telefone, indexOrdenado) => {
                  const indexOriginal = telefones.findIndex(t => 
                    t.ddd === telefone.ddd && 
                    t.numero === telefone.numero && 
                    t.contato === telefone.contato &&
                    t.prioridade === telefone.prioridade
                  );
                  const isDragging = draggedIndex === indexOrdenado;
                  const isDragOver = dragOverIndex === indexOrdenado;
                  
                  return (
                <TelefoneRow 
                  key={`telefone-${indexOriginal}-${telefone.prioridade}`} 
                  data-telefone-row
                  $hasDragHandle={telefones.length > 1}
                  $isDragging={isDragging}
                  $isDragOver={isDragOver}
                  onDragOver={(e) => handleDragOver(e, indexOrdenado)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, indexOrdenado)}
                >
                  {telefones.length > 1 && (
                    <DragHandle
                      title="Arraste para reordenar"
                      draggable={!saving}
                      onDragStart={(e) => handleDragStart(e, indexOrdenado)}
                      onDragEnd={handleDragEnd}
                    >
                      <TbArrowsMoveVertical />
                    </DragHandle>
                  )}
                  <Field>
                    <Label>DDD</Label>
                    <Input
                      type="text"
                      value={telefone.ddd || ''}
                      onChange={(e) => handleTelefoneDDDChange(indexOriginal, e.target.value)}
                      onKeyDown={(e) => {
                        // Bloquear digitação de números se já tiver 2 dígitos
                        const currentValue = (telefone.ddd || '').replace(/\D/g, '');
                        if (currentValue.length >= 2 && 
                            e.key >= '0' && e.key <= '9' && 
                            !e.ctrlKey && !e.metaKey &&
                            !['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      onPaste={(e) => {
                        // Interceptar colar e limitar a 2 dígitos
                        e.preventDefault();
                        const pastedText = e.clipboardData.getData('text');
                        const apenasNumeros = pastedText.replace(/\D/g, '').slice(0, 2);
                        if (apenasNumeros.length > 0) {
                          handleTelefoneDDDChange(indexOriginal, apenasNumeros);
                        }
                      }}
                      placeholder="55"
                      maxLength={2}
                      inputMode="numeric"
                    />
                  </Field>
                  <Field>
                    <Label>Número</Label>
                    <Input
                      type="text"
                      value={telefone.numero}
                      onChange={(e) => handleTelefoneNumeroChange(indexOriginal, e.target.value)}
                      placeholder="99999-9999"
                      maxLength={10}
                      inputMode="numeric"
                    />
                  </Field>
                  <Field>
                    <Label>Contato</Label>
                    <Input
                      type="text"
                      value={telefone.contato}
                      onChange={(e) => handleTelefoneChange(indexOriginal, 'contato', e.target.value)}
                      placeholder="Nome do responsável"
                    />
                  </Field>
                  <TelefoneActions>
                    <SmallButton
                      type="button"
                      $danger
                      $iconOnly
                      onClick={() => removerTelefone(indexOriginal)}
                      disabled={telefones.length === 0}
                      title="Remover telefone"
                    >
                      <FiTrash2 />
                    </SmallButton>
                  </TelefoneActions>
                </TelefoneRow>
                  );
                })}

              <SmallButton type="button" onClick={adicionarTelefone}>
                <FiPlus />
                Adicionar telefone
              </SmallButton>
            </TelefoneList>
          </Section>

          {error && <ErrorMessage>{error}</ErrorMessage>}

          <Actions>
            <Button type="button" onClick={handleCancel} disabled={saving || deleting}>
              Cancelar
            </Button>
            {isEditMode && (
              <Button 
                type="button" 
                $danger
                onClick={handleDeleteClick} 
                disabled={saving || deleting}
              >
                <FiTrash2 />
                Excluir
              </Button>
            )}
            <Button type="submit" $success disabled={saving || deleting}>
              {saving ? <FiLoader /> : <FiSave />}
              {saving ? 'Salvando...' : 'Salvar'}
            </Button>
          </Actions>
        </Form>
      </Container>

      {/* Modal de confirmação de exclusão */}
      {showDeleteModal && (
        <ModalOverlay>
          <ModalContent>
            <ModalTitle>Confirmar Exclusão</ModalTitle>
            <ModalMessage>
              Tem certeza que deseja excluir as informações da revenda? Esta ação não pode ser desfeita e irá excluir:
              <br /><br />
              • As informações da revenda <strong>{registroCarregado?.clientes?.nome_fantasia || registroCarregado?.clientes?.nome_social || 'deste cliente'}</strong>
              <br />
              • Todos os telefones cadastrados
              <br />
              • Todos os horários de funcionamento
              <br />
              • Todos os dados relacionados
            </ModalMessage>
            <ModalActions>
              <ModalButton onClick={handleCancelDelete} disabled={deleting}>
                Cancelar
              </ModalButton>
              <ModalButton $danger onClick={handleDelete} disabled={deleting}>
                <FiTrash2 />
                {deleting ? 'Excluindo...' : 'Sim, Excluir'}
              </ModalButton>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}
    </Layout>
  );
};

export default ClienteSobreForm;


